import { DatePipe } from '@angular/common';
import { Injectable } from "@angular/core";
import { HorizontalListData, HorizontalListFactory } from "common";
import { EntityStatusLabel } from "../../entity/entity.model";
import { SalesforceUrlPipe } from "../../shared/pipes/salesforce-url.pipe";
import { BrokerCommissionPlanLabel, BrokerExData, BrokerPricingPlanLabel, BrokerTierLabel, BrokerTypeLabel } from "../broker.model";

@Injectable()
export class BrokerProfileAdapter {
	constructor(
		private readonly salesforceUrlPipe: SalesforceUrlPipe,
		private readonly datePipe: DatePipe,
	) { }

	adaptStatusListData = (data: BrokerExData): HorizontalListData[] => {
		return [
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Status',
				displayedValue: EntityStatusLabel.get(data?.status),
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Created on',
				displayedValue: this.formatDate(data?.createdOn) || "-",
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Created by',
				displayedValue: data?.createdBy || "-",
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Updated on',
				displayedValue: this.formatDate(data?.updatedOn) || "-",
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Updated by',
				displayedValue: data?.updatedBy || "-",
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Contract date',
				displayedValue: this.formatDate(data?.contractDate) || "-",
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Active',
				displayedValue: data?.active ? "Active" : "Inactive",
			}),
		]
	}

	adaptCommissionListData = (data: BrokerExData): HorizontalListData[] => {
		return [
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Pricing plan',
				displayedValue: BrokerPricingPlanLabel.get(data?.pricingPlan),
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Commission plan',
				displayedValue: BrokerCommissionPlanLabel.get(data?.commission?.plan),
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Initial commission',
				displayedValue: this.formatPercent(data?.commission?.initialDrawCommission),
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Initial period',
				displayedValue: data?.commission?.initialCommissionPeriod?.toString(),
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Initial cap',
				displayedValue: this.formatPercent(data?.commission?.initialCommissionCap),
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Residual commission',
				displayedValue: this.formatPercent(data?.commission?.residualDrawCommission),
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Residual period',
				displayedValue: data?.commission?.residualCommissionPeriod?.toString(),
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Clawback period',
				displayedValue: data?.commission?.defaultClawbackPeriod?.toString(),
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Prepayment clawback period',
				displayedValue: data?.commission?.defaultPrepaymentClawbackPeriod?.toString(),
			}),
		]
	}

	adaptBrandingListData = (data: BrokerExData): HorizontalListData[] => {
		const branding = data?.branding;

		return [
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Supported',
				displayedValue: branding?.supported ? "True" : "False",
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Subdomain',
				displayedValue: branding?.subdomain || "-",
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'LocCalculatorCss',
				displayedValue: branding?.locCalculatorCss ? "True" : "False",
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'OfferEmailCss',
				displayedValue: branding?.offerEmailCss ? "True" : "False",
			}),
		]
	}

	adaptOtherInfoListData = (data: BrokerExData): HorizontalListData[] => {
		return [
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Broker ID',
				displayedValue: data?.brokerId?.toString(),
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Type',
				displayedValue: BrokerTypeLabel.get(data?.type),
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Tier',
				displayedValue: BrokerTierLabel.get(data?.tier),
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Established date',
				displayedValue: this.formatDate(data?.establishedDate) || "-",
			}),
			HorizontalListFactory.createKeyValueExternalLinkItem({
				title: 'Salesforce account ID',
				displayedValue: data?.uniqueId,
				externalLink: this.salesforceUrlPipe.transform(data?.uniqueId),
				isValueHidden: !data?.uniqueId
			}),
		]
	}

	adaptBrokerActivityListData = (data: BrokerExData): HorizontalListData[] => {
		return [
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Total users',
				displayedValue: data?.usersCount?.toString(),
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Total submissions',
				displayedValue: data?.applicationsCountTotal?.toString(),
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Submissions last 7 days',
				displayedValue: data?.applicationsCountLast7Days?.toString(),
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Submissions last 30 days',
				displayedValue: data?.applicationsCountLast30Days?.toString(),
			}),
		]
	}

	private formatDate(date: Date): string {
		return this.datePipe.transform(date);
	}

	private formatPercent(value: number): string {
		const percentValue = `${value}%`;
		return value ? percentValue : "-";
	}
}
